import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Tag } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const CourierStatus = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const [statuses,         setStatuses]         = useState([]);
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [showModal,        setShowModal]        = useState(false);
  const [editMode,         setEditMode]         = useState(false);
  const [submitLoading,    setSubmitLoading]    = useState(false);
  const [errors,           setErrors]           = useState({});

  const [formData, setFormData] = useState({
    CourierStatusID: null, StatusName: '', StatusColor: '#000000', StatusOrder: 0, IsActive: true,
  });

  useEffect(() => { fetchStatuses(); }, []);
  useEffect(() => { filterStatuses(); }, [searchTerm, statuses]);

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/CourierStatus.php');
      if (res.data.success) setStatuses(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    } finally { setLoading(false); }
  };

  const filterStatuses = () => {
    if (!searchTerm.trim()) { setFilteredStatuses(statuses); return; }
    setFilteredStatuses(statuses.filter(s =>
      s.StatusName.toLowerCase().includes(searchTerm.toLowerCase())
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.StatusName.trim()) errs.StatusName = 'Status name is required';
    else if (formData.StatusName.length > 50) errs.StatusName = 'Status name must be 50 characters or less';
    if (!formData.StatusColor || !/^#[0-9A-Fa-f]{6}$/.test(formData.StatusColor))
      errs.StatusColor = 'A valid color is required';
    if (formData.StatusOrder < 0 || isNaN(formData.StatusOrder))
      errs.StatusOrder = 'Order must be 0 or greater';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/CourierStatus.php', {
        ...formData, IsActive: formData.IsActive ? 1 : 0, StatusOrder: parseInt(formData.StatusOrder, 10),
      });
      if (res.data.success) {
        alert(editMode ? 'Courier status updated successfully!' : 'Courier status created successfully!');
        closeModal(); fetchStatuses();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Error saving courier status. Please try again.'));
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (status) => {
    setFormData({ CourierStatusID: status.CourierStatusID, StatusName: status.StatusName, StatusColor: status.StatusColor || '#000000', StatusOrder: status.StatusOrder || 0, IsActive: status.IsActive === 1 });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this courier status?')) return;
    try {
      const res = await apiClient.delete('/CourierStatus.php', { data: { CourierStatusID: id } });
      if (res.data.success) { alert('Courier status deleted successfully!'); fetchStatuses(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || 'Error deleting courier status. Please try again.'));
    }
  };

  const resetForm    = () => { setFormData({ CourierStatusID: null, StatusName: '', StatusColor: '#000000', StatusOrder: 0, IsActive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const isValidColor = (hex) => /^#[0-9A-Fa-f]{6}$/.test(hex);

  const getContrastColor = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#000000' : '#ffffff';
  };

  const inputCls = (err) =>
    `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none
    transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
    ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Tag size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Courier Status Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage courier delivery statuses</p>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0
            text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}
        >
          <Plus size={17} />
          Add Status
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
          transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by status name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium
              placeholder:text-gray-400 placeholder:font-normal"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredStatuses.length} {filteredStatuses.length === 1 ? 'status' : 'statuses'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading courier statuses...
          </div>
        ) : filteredStatuses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <Tag size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No courier statuses found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Order', 'Status Name', 'Color', 'Active', 'Created At', 'Actions'].map(h => (
                    <th key={h}
                      className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredStatuses.map(status => (
                  <tr key={status.CourierStatusID}
                    className="border-b border-gray-100 transition-all duration-150
                      hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-mono text-center text-gray-600">{status.StatusOrder}</td>
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{status.StatusName}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <span
                          className="w-7 h-7 rounded-md border-2 border-gray-200 flex-shrink-0"
                          style={{ backgroundColor: status.StatusColor || '#000000' }}
                        />
                        <span className="font-mono text-[12px] text-gray-500">{status.StatusColor}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        status.IsActive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {status.IsActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {status.CreatedAt ? new Date(status.CreatedAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(status)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150
                            hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(status.CourierStatusID)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-red-100 border-red-400 text-red-600 transition-all duration-150
                            hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[540px] shadow-2xl border border-gray-200 overflow-hidden"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
            >
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0"
                style={{ color: '#fff' }}>
                <Tag size={18} />
                {editMode ? 'Edit Courier Status' : 'Add New Courier Status'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0
                  bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Name + Order */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Status Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.StatusName}
                    onChange={e => setFormData({ ...formData, StatusName: e.target.value })}
                    maxLength="50"
                    placeholder="e.g., In Transit, Delivered"
                    className={inputCls(errors.StatusName)}
                  />
                  {errors.StatusName && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.StatusName}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Display Order <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.StatusOrder}
                    onChange={e => setFormData({ ...formData, StatusOrder: parseInt(e.target.value, 10) || 0 })}
                    min="0"
                    placeholder="0"
                    className={inputCls(errors.StatusOrder) + ' text-right'}
                  />
                  {errors.StatusOrder && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.StatusOrder}</p>}
                </div>
              </div>

              {/* Color + Preview */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Status Color <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="color"
                      value={formData.StatusColor}
                      onChange={e => setFormData({ ...formData, StatusColor: e.target.value.toUpperCase() })}
                      className="w-12 h-11 border-2 border-gray-200 rounded-lg p-0.5 cursor-pointer flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={formData.StatusColor}
                      onChange={e => setFormData({ ...formData, StatusColor: e.target.value })}
                      maxLength="7"
                      placeholder="#000000"
                      className={inputCls(errors.StatusColor)}
                    />
                  </div>
                  {errors.StatusColor && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.StatusColor}</p>}
                </div>

                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Preview</label>
                  <div className="flex items-center h-11">
                    <span
                      className="inline-flex items-center justify-center px-4 py-1.5 rounded-full text-[13px] font-bold uppercase tracking-wide border-2 border-black/10"
                      style={{
                        backgroundColor: isValidColor(formData.StatusColor) ? formData.StatusColor : '#000000',
                        color: isValidColor(formData.StatusColor) ? getContrastColor(formData.StatusColor) : '#ffffff',
                      }}
                    >
                      {formData.StatusName.trim() || 'Status Name'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Active */}
              <div className="mb-6">
                <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                  border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                  <input
                    type="checkbox"
                    checked={formData.IsActive}
                    onChange={e => setFormData({ ...formData, IsActive: e.target.checked })}
                    className="w-5 h-5 cursor-pointer accent-[#0d1c6b]"
                  />
                  <span className="text-sm font-semibold text-gray-700">Active</span>
                </label>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg
                    cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300
                    disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer
                    transition-all duration-200 hover:-translate-y-0.5
                    hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)]
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default CourierStatus;